import axios from 'axios'
import camelcaseKeys from 'camelcase-keys'

export type FacebookAdAccount = {
  id: string
  name: string
  // 由於既有邏輯只判斷是否存在 business 屬性，故不細訂出所有細節，有需要可以參考文件：
  // https://developers.facebook.com/docs/marketing-api/reference/business/
  business?: Record<string, unknown>
}

// Meta Graph/Marketing API 版本。升版時只改這一行 —— whitelist 與 url 預設值都由它
// 衍生，避免兩處各自漂移。
//
// ⚠️ 後端有對應設定（lego script/exporter/helm/*-values.yaml 的 facebookAdsHost），
//    兩邊要一起升。此處原本停在 v17.0，自 2023-07 起沒跟上後端的兩次升版；該版本
//    早在 2024-05-14 就已下線，只是端點無 breaking change、被 Meta 的 auto-upgrade
//    接住才一直沒壞。版本與下線日整理見 cdp-wiki wiki/infrastructure/ads-api-version-sunsets.md
const FB_GRAPH_API_VERSION = 'v25.0'
const FB_GRAPH_API_BASE = `https://graph.facebook.com/${FB_GRAPH_API_VERSION}`

const whitelist = [FB_GRAPH_API_BASE]

const isWhitelisted = (url: string): boolean =>
  whitelist.some(whitelistedUrl => url.startsWith(whitelistedUrl))

export const fetchFacebookAdsAccounts = async (
  userId: string,
  accessToken: string,
  accounts: FacebookAdAccount[] = [],
  // https://developers.facebook.com/docs/graph-api/reference/user/adaccounts/
  url: string = `${FB_GRAPH_API_BASE}/${userId}/adaccounts`
): Promise<FacebookAdAccount[] | undefined> => {
  try {
    if (!isWhitelisted(url)) {
      throw new Error('URL is not whitelisted')
    }

    const res = await axios.get<{
      data: FacebookAdAccount[]
      paging?: { next: string }
    }>(url, {
      params: {
        access_token: accessToken,
        fields: 'name,account_id,business',
      },
    })

    const camelCasedData = camelcaseKeys(res.data, { deep: true })

    if (!camelCasedData.data) {
      return []
    }

    // fetch business account
    const businessAccounts = camelCasedData.data.filter(
      acc => acc.business !== undefined
    )

    accounts = accounts.concat(businessAccounts)

    // Facebook 使用 cursor-based 的 pagination，只要有 next 屬性就遞迴呼叫直到取得所有資料
    // https://developers.facebook.com/docs/graph-api/results?locale=en_US
    if (res.data.paging?.next) {
      return fetchFacebookAdsAccounts(
        userId,
        accessToken,
        accounts,
        res.data.paging.next
      )
    }

    return accounts
  } catch (e) {
    return []
  }
}
